import React from 'react';
import MessageBubble from './MessageBubble';
import EmptyState from './EmptyState';
import TypingIndicator from './TypingIndicator';
import ThinkingLabel from './ThinkingLabel';

const ChatWindowContent = ({
  messages,
  selectedChat,
  isLoadingMessages = false,
  onQuestionClick,
  chatContainerRef,
  hasUserSentMessage,
  newMessageId,
  hoveredMessageId,
  setHoveredMessageId,
  copyStatus,
  isSpeaking,
  speakingMessageId,
  showReasoningMap,
  reasoningTimers,
  isDeepSeekReasoningSelected,
  onSpeak,
  onCopy,
  onArtifactOpen,
  onImageClick,
  onToggleReasoning,
  onEditMessage,
  editingMessage,
  onCancelEdit,
  selectedModel,
  onMessageSent,
  updateChatTitle,
  hasSubscription,
  messageLimit,
  applyEditAndTruncate,
}) => {
  if (messages.length === 0) {
    return (
      <div ref={chatContainerRef} className="flex-grow p-4">
        {isLoadingMessages && selectedChat ? (
          // Show loading indicator when switching to an existing chat
          <div className="h-full flex flex-col items-center justify-center">
            <div className="flex items-center space-x-2">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-600 dark:border-gray-400"></div>
              <span className="text-gray-600 dark:text-gray-400">Loading messages...</span>
            </div>
          </div>
        ) : (
          // Show empty state for new chats
          <EmptyState chatId={selectedChat?.id} />
        )}
      </div>
    );
  }

  return (
    <div ref={chatContainerRef} className="flex-grow p-4">
      <div className="max-w-3xl mx-auto space-y-4">
        {messages.map((message) => (
          <MessageBubble
            key={message.id}
            message={message}
            isNewMessage={message.id === newMessageId}
            hoveredMessageId={hoveredMessageId}
            setHoveredMessageId={setHoveredMessageId}
            copyStatus={copyStatus}
            isSpeaking={isSpeaking}
            speakingMessageId={speakingMessageId}
            showReasoningMap={showReasoningMap}
            reasoningTimers={reasoningTimers}
            onSpeak={onSpeak}
            onCopy={onCopy}
            onArtifactOpen={onArtifactOpen}
            onImageClick={onImageClick}
            onToggleReasoning={onToggleReasoning}
            onEditMessage={onEditMessage}
            editingMessage={editingMessage}
            onCancelEdit={onCancelEdit}
            selectedChat={selectedChat}
            selectedModel={selectedModel}
            onMessageSent={onMessageSent}
            updateChatTitle={updateChatTitle}
            hasSubscription={hasSubscription}
            messageLimit={messageLimit}
            applyEditAndTruncate={applyEditAndTruncate}
            chatMessages={messages}
          />
        ))}

        {/* Conditional typing indicators based on last message state */}
        {(() => {
          const last = messages[messages.length - 1];
          if (!last) return null;

          // After web search completes, keep the typing indicator while the answer starts.
          if (
            last.role === 'assistant' &&
            last.streaming &&
            last.webSearchStatus === 'completed' &&
            !last.text &&
            !last.reasoning_text
          ) {
            return (
              <div className="flex justify-start">
                {(last.reasoning_pending || (last.role === 'user' && last.is_thinking) || isDeepSeekReasoningSelected) ? <ThinkingLabel /> : <TypingIndicator />}
              </div>
            );
          }

          const showGlobalThinking =
            (last.role === 'user' && hasUserSentMessage) ||
            (last.role === 'assistant' &&
              last.streaming &&
              !last.isImageGenerating &&
              !last.text &&
              !last.reasoning_text &&
              !last.isWebSearching);

          if (!showGlobalThinking) return null;

          // Show a waiting status until the provider supplies visible reasoning or an answer.
          return (
            <div className="flex justify-start">
              {(last.reasoning_pending || (last.role === 'user' && last.is_thinking) || isDeepSeekReasoningSelected) ? <ThinkingLabel /> : <TypingIndicator />}
            </div>
          );
        })()}

        {/* Bottom spacer to allow scrolling the last message to the top of the viewport */}
        {hasUserSentMessage && (
          <div className="arrow-chat-bottom-spacer" aria-hidden="true" />
        )}
      </div>
    </div>
  );
};

export default ChatWindowContent;
